import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Headset, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBanco } from "@/hooks/useBanco";
import { BANCO_IN_STANDBY } from "@/lib/bancoAccesso";
import { ORARIO_BANCO, statoBanco } from "@/lib/orarioBanco";
import {
  CHIAVE_SOGLIA,
  contaConversazione,
  leggiSoglia,
  serveSalvagente,
  SOGLIA_PREDEFINITA,
} from "@/lib/salvagente";
import type { ChatMessage } from "@/lib/types";

/**
 * L'ULTIMO SALVAGENTE (Umberto 23/09, Davide 24/09/2026).
 *
 * Sotto l'ultima risposta, e SOLO quando la regola dice di si' (N risposte
 * senza «mi ha aiutato» nella stessa conversazione, o due richieste esplicite
 * di una persona), compare la riga rossa: «Se non sono riuscito a esserti
 * utile, ti metto in contatto con un tecnico dell'ufficio Datacom». Chi usa
 * bene l'app non la vede mai. Non si pubblicizza da nessuna parte.
 *
 * Qui si DECIDE SE MOSTRARE, con la stessa regola del server (lib/salvagente).
 * Chi decide davvero e' la funzione `richiedi-operatore`: riconta dal
 * database, guarda l'orario di Roma, apre la chat col banco o prenota il
 * richiamo, e avvisa Datacom per mail.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Giudizio {
  esito: string;
  created_at: string;
}

interface EsitoRichiesta {
  ok?: boolean;
  gia_aperta?: boolean;
  stato?: string;
  aperto?: boolean;
  per_il?: string | null;
  ora?: string;
  messaggio?: string;
  error?: string;
}

function tabellaAssente(e: { code?: string; message?: string } | null): boolean {
  if (!e) return false;
  const m = `${e.code ?? ""} ${e.message ?? ""}`;
  return m.includes("42P01") || /does not exist|schema cache/i.test(m);
}

export function Salvagente({
  conversationId,
  messages,
  loading,
}: {
  conversationId: string | null;
  messages: ChatMessage[];
  /** Il motore sta ancora scrivendo: niente riga finche' non ha finito. */
  loading: boolean;
}) {
  const { user, profile } = useAuth();
  const banco = useBanco();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [telefono, setTelefono] = useState(profile?.telefono ?? "");
  const valida = !!conversationId && UUID.test(conversationId) && !!user;

  const { data: giudizi = [] } = useQuery({
    queryKey: ["giudizi", conversationId],
    enabled: valida && !BANCO_IN_STANDBY,
    queryFn: async (): Promise<Giudizio[]> => {
      const { data, error } = await supabase
        .from("giudizi")
        .select("esito, created_at")
        .eq("conversation_id", conversationId!)
        .limit(200);
      if (error) {
        if (tabellaAssente(error)) return [];
        throw error;
      }
      return (data ?? []) as Giudizio[];
    },
  });

  // null = la migrazione del 24/09 non e' ancora applicata: il salvagente
  // resta nascosto, perche' la funzione non avrebbe dove scrivere.
  const { data: soglia } = useQuery({
    queryKey: ["impostazioni", CHIAVE_SOGLIA],
    enabled: !!user && !BANCO_IN_STANDBY,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase.from("impostazioni").select("valore").eq("chiave", CHIAVE_SOGLIA).maybeSingle();
      if (error) {
        if (tabellaAssente(error)) return null;
        throw error;
      }
      return data ? leggiSoglia(data.valore) : SOGLIA_PREDEFINITA;
    },
  });

  const esito = useMemo(() => {
    const contati = messages
      .filter((m) => !m.fromMock)
      .map((m) => ({ role: m.role, content: m.content, quando: m.createdAt }));
    const g = giudizi.map((x) => ({ esito: x.esito, quando: new Date(x.created_at).getTime() }));
    return serveSalvagente(contaConversazione(contati, g), soglia ?? SOGLIA_PREDEFINITA);
  }, [messages, giudizi, soglia]);

  const chiedi = useMutation({
    mutationFn: async (): Promise<EsitoRichiesta> => {
      const { data, error } = await supabase.functions.invoke("richiedi-operatore", {
        body: { conversation_id: conversationId, telefono: telefono.trim() },
      });
      if (error) {
        // La funzione risponde con un corpo JSON anche quando dice di no.
        let dettaglio = "";
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const corpo = (await ctx.json()) as EsitoRichiesta;
            dettaglio = corpo?.error === "troppo_presto" ? "Non è ancora il momento: prova ancora con l'assistente." : corpo?.error ?? "";
          }
        } catch {
          dettaglio = "";
        }
        throw new Error(dettaglio || error.message || "richiesta non riuscita");
      }
      return (data ?? {}) as EsitoRichiesta;
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["richiesta-operatore"] });
      void qc.invalidateQueries({ queryKey: ["banco-filo"] });
      setAperto(false);
      if (d.aperto) {
        toast.success("Sei in linea con i tecnici Datacom", { description: d.messaggio });
        navigate("/banco");
      } else {
        toast.success("Richiamo prenotato", { description: d.messaggio, duration: 15000 });
      }
    },
    onError: (e: Error) => toast.error("Non sono riuscito a passarti ai tecnici", { description: e.message }),
  });

  if (BANCO_IN_STANDBY || loading || !valida || soglia === null || soglia === undefined) return null;
  const ultimo = messages[messages.length - 1];
  if (!ultimo || ultimo.role !== "assistant") return null;

  // Una richiesta e' gia' aperta: la strada e' la chat col banco, non un'altra richiesta.
  if (banco.richiesta) {
    const r = banco.richiesta;
    return (
      <div className="mx-auto w-full max-w-3xl px-4 pb-3">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-surface/60 px-4 py-3 text-sm">
          <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 text-muted-foreground">
            {r.stato === "prenotata"
              ? "Hai un richiamo prenotato con i tecnici Datacom. Intanto puoi scrivere nella chat col banco."
              : "Sei in contatto con i tecnici dell'ufficio Datacom."}
          </span>
          <Button size="sm" variant="secondary" onClick={() => navigate("/banco")}>
            Apri la chat col banco
          </Button>
        </div>
      </div>
    );
  }

  if (esito === "no") return null;
  const stato = statoBanco();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-3">
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
        <p className="text-foreground">
          Se non sono riuscito a esserti utile, ti metto in contatto con un tecnico
          dell&apos;ufficio Datacom.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button
            size="sm"
            onClick={() => setAperto(true)}
            className="gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Headset className="h-4 w-4" />
            Parla con un tecnico
          </Button>
          <span className="text-xs text-muted-foreground">
            {stato.aperto ? "Il banco è aperto adesso: ti risponde una persona." : "Ora il banco è chiuso: prenoti un richiamo."}
          </span>
        </div>
      </div>

      <Dialog open={aperto} onOpenChange={(o) => !chiedi.isPending && setAperto(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Un tecnico dell&apos;ufficio Datacom</DialogTitle>
            <DialogDescription>
              {stato.aperto
                ? "In orario d'ufficio la chat si apre subito e ti risponde una persona di Datacom, con le tue ultime domande già davanti."
                : `Il banco riapre ${ORARIO_BANCO}. Lasci il numero e ti richiamano alla riapertura; intanto puoi scrivere nella chat col banco.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="salvagente-telefono">Il tuo numero di telefono</Label>
            <Input
              id="salvagente-telefono"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="es. 333 1234567"
            />
            <p className="text-xs text-muted-foreground">Serve solo se i tecnici devono richiamarti.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setAperto(false)} disabled={chiedi.isPending}>
              Annulla
            </Button>
            <Button onClick={() => chiedi.mutate()} disabled={chiedi.isPending} className="gap-1.5">
              {chiedi.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Headset className="h-4 w-4" />}
              {stato.aperto ? "Apri la chat col tecnico" : "Prenota il richiamo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
